/**
 * AgentEnquiryForm
 * 
 * @Author: Focci
 * @Date: 2024-01-17 10:30:37
 * @Last Modified by: Focci
 * @Last Modified time: 2024-01-17 10:30:37
 */

// import PropTypes from 'prop-types'
import ControllerFiled from '@comp/form/ControllerFiled'
import StyledInput from '@comp/form/StyledInput'
import { Button, CircularProgress } from '@comp/material'
import EnquiryOptions from '@comp/page/property/agent-card/EnquiryOptions'
import { useEnquiryInfo } from '@hook/useEnquiryInfo'
import useTracking from '@hook/useTracking'
import { clean, getPhonePrefix, houseToGta, isAU, isValid } from '@lib/utils'
import { filter, map } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { useCallback, useState } from 'react'

function getBodyContent(code, option) {
  const opt = filter(option, (f) => isValid(f))
  const tmp = [`I am interested in this property${code ? ` ref:${code}` : ''}`]

  if(opt.length > 0) {
    tmp.push(`and ${opt.join(', ')}`)
  }

  tmp.push('please send me more information')
  
  return tmp.join(', ')
}

export default function AgentEnquiryForm({
  type = 'house',
  id = null,
  agentId = '',
  code,
  defaultSpecs = [],
  defaultSpecLabel,
  onSuccess = () => {},
  house
}) {
  const te = useTranslations('enquiry')
  const { pushTrack, pushTrackListing } = useTracking()

  // 咨询类型选项
  const optionBody = isValid(defaultSpecLabel) ? [defaultSpecLabel] : []
  const [body, setBody] = useState(getBodyContent(code, optionBody))

  const { 
    spec, form, loading, userName, userMobile, emailRules, 
    userEmail, setSpec, submitEnquiry,
  } = useEnquiryInfo({ defaultSpecs })
  
  // https://7thave.atlassian.net/browse/HT-3273
  // 发给所有中介，后端反馈不用传 agentId
  const handleConfirm = useCallback(async (d) => {
    const param = clean({ 
      ...d,
      id, 
      type, 
      spec: spec.join(','), 
      countryCode: getPhonePrefix()
    })
    
    pushTrack('click_email_sent', { houseId: id, agentId, agencyOfficeId: house?.office?.id })
    pushTrackListing('click_email_sent', house)
    isAU && pushTrack('homely_email_enquiry', houseToGta(house))
    
    submitEnquiry(param).then(() => onSuccess())
  }, [
    id, agentId, type, spec, house, 
    pushTrackListing, pushTrack, submitEnquiry, onSuccess
  ])

  const handleEntiryOptionChange = useCallback(({ value, data }) => {
    const ctx = getBodyContent(code, map(data, 'en'))
    form.setValue('body', ctx)

    setBody(ctx)
    setSpec(value)
  }, [setSpec, code, form])

  return (
    <div>
      <EnquiryOptions
        multiple
        type={type}
        value={spec}
        onChange={handleEntiryOptionChange}
        wrapperClassName="min-h-[31px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-2 gap-y-1"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mt-2">
        <div className="flex flex-col gap-y-2">
          <ControllerFiled
            name="name"
            defaultValue={userName}
            inputProps={{ 
              placeholder: te('yourName'), 
              variant: 'outline' 
            }}
            Input={StyledInput}
            form={form}
            rules={{ required: 'Name is required!' }}
          />
          <ControllerFiled
            name="phoneNumber"
            defaultValue={userMobile}
            inputProps={{ 
              placeholder: te('contatPhone'), 
              variant: 'outline' 
            }}
            Input={StyledInput}
            form={form}
          />
          <ControllerFiled
            name="email"
            defaultValue={userEmail}
            inputProps={{ 
              placeholder: te('typeYourEmail'), 
              variant: 'outline' 
            }}
            Input={StyledInput}
            form={form}
            rules={emailRules}
          />
        </div>
        <div className="flex flex-col gap-y-2">
          <ControllerFiled
            name="body"
            defaultValue={body}
            inputProps={{ 
              placeholder: te('yourMessage'), 
              variant: 'outline',
              multiline: true,
              sx: {
                '& .MuiInputBase-input': { minHeight: '6.7rem' }
              }
            }}
            Input={StyledInput}
            form={form}
          />
        </div>
      </div>
      <div className="mt-4 max-w-[25rem] mx-auto">
        <Button
          size="large"
          onClick={form.handleSubmit(handleConfirm)}
          disabled={loading}
          className="w-full"
        >
          {loading 
            ? <div><CircularProgress size="1.2em" /></div>
            : te('sendEmail')}
        </Button>
      </div>
    </div>
  )
}
